import React, { useState, useMemo, useEffect, useRef } from "react";
import { Button, Tooltip, TableProps, message } from "antd";
import { DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import "./Ready.css";
import CsvDownload from "./CsvDownload/CsvDownload";
// import Loader from "../../../Components/Loader/Loader";
import {
  GetInvoicesService_NEW,
  UpdateInvoiceFlagService,
} from "../../../Services/InvoiceService/InvoiceService";
import { useDispatch, useSelector } from "react-redux";
import AlertMessage from "../../../Components/AlertMessage/AlertMessage";
import CommonTable from "../../../Components/CustomTable/CustomTable";
import ColumnSettings from "../../../Components/ColumnFilter/ColumnFilter";
import FilterBtn from "../../../Components/CustomButtons/FilterBtn/FilterBtn";
// import { FormatDateForReport } from "../../../Components/CommonUI/DateFormatter";
import { InvoiceFieldMap } from "../../../Services/InvoiceService/constants";
import { useInvoiceActions } from "../../../hooks/useInvoiceActions";
import { IoSettingsSharp } from "react-icons/io5";
import { InvoiceFilter } from "../../../Components/InvoiceFilter";
import { FiChevronDown, FiSearch } from "react-icons/fi";
import AdvancedSearch from "../../../Components/InvoiceSearch/AdvancedSearch";
import { toInvoiceSearchPayload } from "../../../Components/InvoiceSearch/AdvanceType";
import ConfirmPopup from "../../../Components/ConfirmPopup/ConfirmPopup";
import { setSelectedInvoice } from "../../../Reducer/SelectedInvoiceReducer";
import SelectedClientCard from "../../../Components/SelectedClientCard/SelectedClientCard";
import ReadyInvoiceView from "./ReadyInvoiceView/ReadyInvoiceView";
import { InvoiceFilterFieldsPayload } from "../../../Components/InvoiceFilter/InvoiceFilter.fields";
import { ActiveFilterBar } from "../../../Components/InvoiceFilter/ActiveFilterBar";
import { formatForInput } from "../../../Utils/datepickerFormat";
import { fetchData } from "pdfjs-dist";
import ToolsMenu, { BulkEditResult } from "../3Inbox/Tools/Toolsmenu"; // adjust path to where Toolsmenu really lives
import { BulkEditOptions } from "../3Inbox/BulkeditPopup/BulkEditPopup"; // keep the real folder casing
import {
  DeleteInvoice,
} from "../../../Services/InvoiceService/InvoiceService";


interface RowData {
  id: number;
  userId: string;
  isFlagged: boolean;
  adminUserId: string | null;
  documentUrl: string | null;
  documentOwner: string;
  type: string;
  invoiceDate: string;
  supplier: string;
  extractedPoNumber: string;
  documentReference: string;
  dueDate: string;
  category: string;
  productOrService: string;
  customer: string;
  markAsRebillable: string;
  description: string;
  discountAmount: number;
  // amount: number;
  currency: string;
  totalAmount: number;
  taxAmount: number;
  netAmount: number;
  payment: number;
  paid: string;
  paymentMethod: string;
  lineItems: Array<string | object>; // Assuming line items could be strings or complex objects
  stageId: number;
  isActive: boolean;
  isDelete: boolean;
  selected: boolean;
  archiveStatus: string;
  documentShortId: string;
}

// Main component
const Ready: React.FC = () => {
  const { makeInvoicesExported, makeInvoicesArchived } = useInvoiceActions();
  const selectedClient = useSelector((state: any) => state.selectedClient);
  var INVOICE_PER_FETCH = 100;
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1); // Current page number
  const [selectedRows, setSelectedRows] = useState<RowData[]>([]); // Track selected rows
  const [selectAll, setSelectAll] = useState(false); // Track "Select All" state
  const [pageSize, setPageSize] = useState(10);
  const [cursor, setCursor] = useState<string>("");
  const [totalInvoiceCount, setTotalInvoiceCount] = useState(0);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const listRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [invoiceView, setInvoiceView] = useState(false);
  // State to control InvoiceFilter popup
  const [isFilterPopupOpen, setIsFilterPopupOpen] = useState(false);

  const [failedView, setFailedView] = useState(false);
  // State to store active filter values
  const [activeFilters, setActiveFilters] = useState<any>(null);
  const [includeDetails, setIncludeDetails] = useState(false);
  const cursorRef = useRef<string>("");
  const savedSort = localStorage.getItem("sortSettings");
  const sortReverseRef = useRef<boolean>(false);
  const sortKeyRef = useRef<keyof typeof InvoiceFieldMap | null>(null);
  const [flagOverrides, setFlagOverrides] = useState<
    Record<string | number, boolean>
  >({});

  const [searchTerm, setSearchTerm] = useState("");
  const debounceRef = useRef<any>(null);
  const savedScrollPositionRef = useRef<number>(0);

  const { makeInvoiceViewed } = useInvoiceActions();
  const dispatch = useDispatch();

  const activeFiltersRef = useRef<any>(null);
  const activeAdvancedSearchRef = useRef<any>(null);

  const [fullinvoices, setFullInvoices] = useState<any[]>([]);

  const [activeAdvancedSearch, setActiveAdvancedSearch] = useState<any>(null);
  const advancedSearchRef = useRef<{ resetForm: () => void }>(null);
  interface SortState {
    key: keyof typeof InvoiceFieldMap | "default" | null;
    direction: boolean;
  }
  const [sortSettings, setSortSettings] = useState<SortState>({
    key: "default",
    direction: false,
  });

  const FetchData = async (
    loadMore = false,
    reverse: boolean = false,
    key: keyof typeof InvoiceFieldMap | null = null,
    filters: any = activeFilters, // Accept filters
    searchFields: any = null, // Accept search fields
    cursorOverride: string | null | undefined = null,
    limit: number = INVOICE_PER_FETCH,
  ) => {
    if (!selectedClient.selectedClient?.userId) return;
    const apiSearchFields = toInvoiceSearchPayload(searchFields);
    try {
      /*
      Call the service to fetch all invoices for a specific client
      First prop specifies the type of request (e.g., all invoices)
      Second prop is a placeholder for the client identifier
       */
      if (!loadMore) setLoading(true);
      // ! Ready stage ID is 2
      const result = await GetInvoicesService_NEW({
        userId: selectedClient.selectedClient.userId,
        after: cursorRef.current || null,
        order: key ?? (savedSort ? JSON.parse(savedSort).key : "default"),
        reversed:
          key !== null
            ? reverse
            : savedSort
              ? JSON.parse(savedSort).direction
              : false,
        stageIds: [21, 22],
        archiveStatuses: ["UnArchived", "Default"],
        limit: limit,
        filterFields: filters,
        searchFields: apiSearchFields,
      });

      if (result?.Items !== undefined) {
        setTotalInvoiceCount(result.TotalItem ?? 0);
        setInvoices((prev) => {
          if (!loadMore) return result.Items;

          const existingIds = new Set(prev.map((item) => item.id));

          const newItems = result.Items.filter(
            (item: any) => !existingIds.has(item.id),
          );

          return [...prev, ...newItems];
        });
        //  Fix cursor logic
        cursorRef.current = result.NextCursor || "";

        setHasMore(result.HasMore);
      }

      const fullresult = await GetInvoicesService_NEW({
        userId: selectedClient.selectedClient.userId,
        after: null, // <-- always null for full fetch
        order: key ?? (savedSort ? JSON.parse(savedSort).key : "default"),
        reversed:
          key !== null
            ? reverse
            : savedSort
              ? JSON.parse(savedSort).direction
              : false,
        stageIds: [21, 22],
        archiveStatuses: ["UnArchived", "Default"],
        limit: result.TotalItem || totalInvoiceCount, // <-- FIX: use fresh result, not stale state
        filterFields: filters,
        searchFields: apiSearchFields,
      });
      setFullInvoices(fullresult?.Items || []);
      setFullInvoices(fullresult.Items);
    } catch (error) {
      /* 
      Log any error that occurs during the data fetch process
      This can be useful for debugging issues such as network failures 
      */
      setLoading(false);
      console.error("Error fetching invoice data:", error);
    } finally {
      setLoading(false);
    }
  };
  const sortInvoices: any = async (
    reverse: boolean,
    key: keyof typeof InvoiceFieldMap | null,
  ) => {
    cursorRef.current = "";
    sortKeyRef.current = key;
    sortReverseRef.current = reverse;
    setIsLoadingMore(false); // start loading
    const newSettings = { key, direction: reverse };
    setSortSettings(newSettings);
    localStorage.setItem("sortSettings", JSON.stringify(newSettings));
    await FetchData(
      false,
      reverse,
      key,
      activeFilters,
      activeAdvancedSearch,
      null,
      invoices.length,
    ); // fetch next batch
    setIsLoadingMore(false);
    setSelectedRows([]); // clear selections on sort
  };

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);

    // reset cursor for new search
    cursorRef.current = "";

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      FetchData(
        false,
        false,
        null,
        activeFilters,
        value ? { searchTerm: value } : null,
      );
    }, 500); // 500ms delay
  };

  useEffect(() => {
    FetchData();
  }, []);
  const resetAndFetch = async () => {
    setCursor(""); // reset cursor
    setInvoices([]); // clear stale data
    setCursor(""); // reset cursor
    setSelectedRows([]); // clear selections
    setSelectAll(false);
    setCurrentPage(1); // back to page 1
    const sortDir = savedSort ? JSON.parse(savedSort).direction : false;
    const sortKey = savedSort ? JSON.parse(savedSort).key : null;
    await FetchData(
      false,
      sortDir,
      sortKey,
      activeFilters,
      activeAdvancedSearch,
    ); // fresh fetch, no loadMore
  };

  const refreshInvoices = async () => {
    cursorRef.current = "";
    setHasMore(true);
    const sortDir = savedSort ? JSON.parse(savedSort).direction : false;
    const sortKey = savedSort ? JSON.parse(savedSort).key : null;
    await FetchData(
      false,
      sortDir,
      sortKey,
      activeFilters,
      activeAdvancedSearch,
      null,
      invoices.length || 100,
    );
  };

  const handleScroll = () => {
    const div = listRef.current;
    if (!div || isLoadingMore || !hasMore) return;

    const scrollTop = div.scrollTop;
    const scrollHeight = div.scrollHeight;
    const clientHeight = div.clientHeight;

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      // scroll bottom close to 100px
      loadMoreInvoicesOnScroll();
    }
  };
  // Logic to handle filter application
  const handleApplyFilters = (filterValues: any) => {
    cursorRef.current = "";
    sortKeyRef.current = null; // ← reset sort
    sortReverseRef.current = false;
    setInvoices([]);
    setActiveFilters(filterValues);
    setIsFilterPopupOpen(false);
    FetchData(false, false, null, filterValues);
  };
  useEffect(() => {
    const div = listRef.current;
    if (!div) return;

    div.addEventListener("scroll", handleScroll);
    return () => div.removeEventListener("scroll", handleScroll);
  }, [listRef, isLoadingMore, hasMore, cursor]); // include cursor dependency

  const loadMoreInvoicesOnScroll = async () => {
    if (hasMore && !isLoadingMore) {
      setIsLoadingMore(true); // start loading
      await FetchData(
        true,
        sortReverseRef.current,
        sortKeyRef.current,
        activeFiltersRef.current,
        activeAdvancedSearchRef.current,
      ); // fetch next batch
      setIsLoadingMore(false);
    }
  };
  const handleInvoiceClick = (row: RowData) => {
    const tableWrapper = document.querySelector(
      ".table-wrapper",
    ) as HTMLElement;
    if (tableWrapper) {
      savedScrollPositionRef.current = tableWrapper.scrollTop;
    }
    sessionStorage.setItem("selectedInvoiceId", row.id.toString());
    dispatch(setSelectedInvoice(row));
    setInvoiceView(true);
  };

  // Calculate the current records based on the page
  const indexOfLastRecord = currentPage * pageSize;
  const indexOfFirstRecord = indexOfLastRecord - pageSize;
  const currentRecords = (invoices || []).slice(
    indexOfFirstRecord,
    indexOfLastRecord,
  );
  const [isOpenAdvance, setIsOpenAdvance] = useState(false);
  const handleSelectRecord = (record: RowData) => {
    setSelectedRows((prevSelectedRows) => {
      const isSelected = prevSelectedRows.some((row) => row.id === record.id);
      const updatedRows: RowData[] = isSelected
        ? prevSelectedRows.filter((row) => row.id !== record.id)
        : [...prevSelectedRows, record];

      setSelectAll(updatedRows.length === invoices.length);
      return updatedRows;
    });
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows([]);
    } else {
      setSelectedRows([...invoices]); //  full objects
    }
    setSelectAll(!selectAll);
  };

  useEffect(() => {
    activeFiltersRef.current = activeFilters;
  }, [activeFilters]);

  useEffect(() => {
    activeAdvancedSearchRef.current = activeAdvancedSearch;
  }, [activeAdvancedSearch]);

  const handlePageChange = (page: number, pageSize: number) => {
    setCurrentPage(page);
    if (pageSize) setPageSize(pageSize);
    // if (currentRecords.length === 0) {
    //   // setSelectAll(false);
    // }
    setSelectAll(false);
  };
  // Headers matching your provided data structure
  const exportDocHeaders = [
    { label: "Invoice Date", key: "invoiceDate" },
    { label: "Type", key: "type" },
    { label: "Document Reference", key: "documentReference" },
    { label: "Supplier", key: "supplier" },
    { label: "Total Amount", key: "totalAmount" },
    { label: "Tax Amount", key: "taxAmount" },
    { label: "Net Amount", key: "netAmount" },
    { label: "ID", key: "id" },
    { label: "Document URL", key: "documentShortId" },
    // { label: "User", key: "documentOwner" },
    // { label: "Admin User ID", key: "adminUserId" },
    { label: "Document Owner", key: "documentOwner" },
    { label: "Extracted PO Number", key: "extractedPoNumber" },
    { label: "Due Date", key: "dueDate" },
    { label: "Category", key: "category" },
    { label: "Product or Service", key: "productOrService" },
    { label: "User", key: "documentOwner" },
    { label: "Mark as Rebillable", key: "markAsRebillable" },
    { label: "Description", key: "description" },
    // { label: "Amount", key: "amount" },
    { label: "Currency", key: "currency" },
    { label: "Discount", key: "discountAmount" },
    { label: "Payment", key: "payment" },
    { label: "Paid", key: "paid" },
    { label: "Payment Method", key: "paymentMethod" },
    { label: "Line Items", key: "lineItems" },
    { label: "Archive Status", key: "archiveStatus" },
    // { label: "Document Short ID", key: "documentShortId" },
    // { label: "Stage ID", key: "stageId" },
    // { label: "Is Active", key: "isActive" },
    // { label: "Is Deleted", key: "isDelete" },
  ];

  const resetAndFetchWithCount = async (count: number) => {
    setCursor("");
    cursorRef.current = "";
    setInvoices([]);
    setSelectedRows([]);
    setSelectAll(false);

    const limitToFetch = Math.max(count, INVOICE_PER_FETCH);
    await FetchData(
      false,
      sortReverseRef.current,
      sortKeyRef.current,
      activeFilters,
      activeAdvancedSearch,
      null,
      limitToFetch,
    );
  };

  const bulkEditOptions: BulkEditOptions = {
    type: [], supplier: [], category: [], currency: [], paid: [],
    paymentMethod: []
  };

// Refetch keeping filters/search/sort and the number of rows already loaded.
// Setting loading=true unmounts ReviewList, which also resets its checkboxes.
const refetchKeepingView = async () => {
  setFailedView(false);
  setFlagOverrides({});
  await resetAndFetchWithCount(invoices.length);
};

const handleFlag = async (ids: number[]) => {
  try {
    await Promise.all(ids.map((id) => UpdateInvoiceFlagService(id, true)));
    AlertMessage({ type: "success", text: "Invoice(s) flagged successfully" });
    await refetchKeepingView();
  } catch {
    AlertMessage({ type: "error", text: "Failed to flag invoice(s)" });
  }
};

const handleUnflag = async (ids: number[]) => {
  try {
    await Promise.all(ids.map((id) => UpdateInvoiceFlagService(id, false)));
    AlertMessage({ type: "success", text: "Invoice(s) unflagged successfully" });
    await refetchKeepingView();
  } catch {
    AlertMessage({ type: "error", text: "Failed to unflag invoice(s)" });
  }
};

const handleDelete = async (ids: number[]) => {
  try {
    await Promise.all(ids.map((id) => DeleteInvoice(id)));
    await refetchKeepingView();
  } catch {
    AlertMessage({ type: "error", text: "Failed to delete invoice(s)" });
  }
};

const handleViewFailedItems = async (ids: number[]) => {
  try {
    const result = await GetInvoicesService_NEW({
      userId: selectedClient.selectedClient.userId,
      ids,
    } as any);
    setInvoices(result?.Items ?? []);
    setTotalInvoiceCount(result?.TotalItem ?? ids.length);
    setHasMore(false);
    cursorRef.current = "";
    setFailedView(true);
  } catch {
    AlertMessage({ type: "error", text: "Failed to load the failed items" });
  }
};

const handleBulkEditComplete = async (_result: BulkEditResult) => {
  await refetchKeepingView();
};

  const moveToArchieve = async () => {
    if (selectedRows.length === 0) {
      AlertMessage({
        text: "Please select at least one invoice to archive.",
        type: "warning",
      });
      return;
    }

    const tableWrapper = document.querySelector(
      ".table-wrapper",
    ) as HTMLElement;
    const savedScrollTop = tableWrapper ? tableWrapper.scrollTop : 0;
    const previouslyLoadedCount = invoices.length;

    try {
      setLoading(true);
      const archivePayload = selectedRows.map((row) => ({
        invoiceId: row.id,
        invoicearchieveStatus: row.archiveStatus,
        invoiceStageId: row.stageId,
      }));
      await makeInvoicesArchived(archivePayload);
      setSelectedRows([]);
    } catch (error) {
      console.error("Error moving invoices to archive:", error);
      AlertMessage({
        text: "Something went wrong while archiving invoices.",
        type: "error",
      });
    } finally {
      setLoading(false);
      await resetAndFetchWithCount(previouslyLoadedCount);
      setTimeout(() => {
        const wrapper = document.querySelector(".table-wrapper") as HTMLElement;
        if (wrapper) wrapper.scrollTop = savedScrollTop;
      }, 300);
    }
  };

  //   const truncateUrl = (url: string | null, maxLength: number = 30) => {
  //   if (!url) return "";
  //   return url.length > maxLength ? `${url.substring(0, maxLength)}...` : url;
  // };
  const getCurrencyCode = (currency: string) => {
    if (!currency) return "";

    const match = currency.match(/\((.*?)\)/);

    return match ? match[1] : currency;
  };

  const exportToXlsx = async () => {
    const tableWrapper = document.querySelector(
      ".table-wrapper",
    ) as HTMLElement;
    const savedScrollTop = tableWrapper ? tableWrapper.scrollTop : 0;
    const previouslyLoadedCount = invoices.length;
    setLoading(true);
    try {
      const headerLabels = exportDocHeaders.map((header) => header.label);

      const keyMapping: (keyof RowData)[] = exportDocHeaders.map(
        (header) => header.key as keyof RowData,
      );

      const formatAmount = (value: any) => {
        const num = parseFloat(value);
        return !isNaN(num) ? Number(num.toFixed(2)) : 0.0;
      };

      const rowData = selectedRows.map((row: RowData) =>
        keyMapping.map((key) => {
          let value: any = row[key];

          // if (key === "documentUrl") {
          //   return truncateUrl(value, 30);
          // }

          // ── Dates: always DD/MM/YYYY ──────────────────────────────────────
          // Export dates as Date objects
          if (key === "invoiceDate" || key === "dueDate") {
            return value ? new Date(value) : null;
          }

          // ── Numeric amount fields ─────────────────────────────────────────
          if (
            [
              "discountAmount",
              "totalAmount",
              "taxAmount",
              "netAmount",
              "payment",
            ].includes(key as string)
          ) {
            return formatAmount(value);
          }
          if (key === "currency") {
            return getCurrencyCode(value);
          }
          if (key === "documentShortId") {
            return `${window.location.origin}/i/${value}`;
          }
          return value ?? "";
        }),
      );

      const clientName =
        selectedClient?.selectedClient?.name || "unknown-client";

      const safeClientName = clientName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, "-");

      const today = new Date();
      const day = String(today.getDate()).padStart(2, "0");
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const year = today.getFullYear();

      const formattedDate = `${day}-${month}-${year}`;

      const worksheetData = [headerLabels, ...rowData];

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      const DEFAULT_WIDTH = 15;
      const AUTO_FIT_LIMIT = 60;

      // Set column widths
      worksheet["!cols"] = worksheetData[0].map((_, colIndex) => {
        const maxLength = worksheetData.reduce((max, row) => {
          let cellValue = row[colIndex] != null ? String(row[colIndex]) : "";

          // Date column format length control
          if (colIndex === 0 || colIndex === 11) {
            cellValue = "dd/mm/yyyy";
          }

          return Math.max(max, cellValue.length);
        }, 0);

        return {
          // Keep default width until content exceeds 60 characters
          wch: Math.min(Math.max(maxLength + 2, DEFAULT_WIDTH), AUTO_FIT_LIMIT),
        };
      });

      // Format date columns
      const dateColumns = [0, 11];

      dateColumns.forEach((colIndex) => {
        for (let row = 1; row < worksheetData.length; row++) {
          const cellRef = XLSX.utils.encode_cell({ r: row, c: colIndex });

          if (worksheet[cellRef]) {
            worksheet[cellRef].z = "dd/mm/yyyy";
          }
        }
      });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Selected Invoices");
      const fileName = `${safeClientName}-${formattedDate}.xlsx`;

      XLSX.writeFile(workbook, fileName);

      await Promise.all;
      makeInvoicesExported(
        selectedRows.map((row) => {
          return {
            invoiceId: row.id,
            invoicearchieveStatus: row.archiveStatus,
            invoiceStageId: row.stageId,
          };
        }),
      );

      if (includeDetails) {
        const archivePayload = selectedRows.map((row) => ({
          invoiceId: row.id,
          invoicearchieveStatus: row.archiveStatus,
          invoiceStageId: row.stageId,
        }));
        await makeInvoicesArchived(archivePayload);
        AlertMessage({
          text: "Invoices exported and archived successfully",
          type: "success",
        });
      }

      await FetchData();
    } catch (error) {
      message.error("Error exporting data. Please try again.");
    } finally {
      setLoading(false);
      await resetAndFetchWithCount(previouslyLoadedCount);
      setOpenPopup(false);
      setIncludeDetails(false);
      setTimeout(() => {
        const wrapper = document.querySelector(".table-wrapper") as HTMLElement;
        if (wrapper) wrapper.scrollTop = savedScrollTop;
      }, 300);
    }
  };

  const exportToCSV = async (includeDetails: boolean) => {
    const tableWrapper = document.querySelector(
      ".table-wrapper",
    ) as HTMLElement;
    const savedScrollTop = tableWrapper ? tableWrapper.scrollTop : 0;
    const previouslyLoadedCount = invoices.length;

    setLoading(true);
    try {
      const formatAmount = (value: any) => {
        const num = parseFloat(value);
        // return !isNaN(num) ? num.toFixed(2) : "0.00";
        return !isNaN(num) ? `'${num.toFixed(2)}` : "'0.00'";
      };

      // Use exportDocHeaders to control column order — same approach as XLSX export
      const headerLabels = exportDocHeaders.map((header) => header.label);
      const keyMapping = exportDocHeaders.map((header) => header.key);

      const rowData = selectedRows.map((row: any) =>
        keyMapping.map((key) => {
          let value: any = row[key];

          // if (key === "documentUrl") {
          //   return truncateUrl(value, 30);
          // }

          if (key === "invoiceDate" || key === "dueDate") {
            return formatForInput(value);
          }

          if (
            [
              "discountAmount",
              "totalAmount",
              "taxAmount",
              "netAmount",
              "payment",
            ].includes(key) &&
            typeof value === "number"
          ) {
            return formatAmount(value);
          }

          if (key === "documentShortId") {
            return `${window.location.origin}/i/${value}`;
          }

          return value !== undefined && value !== null ? value : "";
        }),
      );

      // Build CSV manually from ordered header + rows
      const worksheetData = [headerLabels, ...rowData];
      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

      // Step 4: Convert data to CSV format
      // const csvData = XLSX.utils.json_to_sheet(formattedRows);
      // const csv = XLSX.utils.sheet_to_csv(csvData);

      // // Step 5: Trigger CSV file download
      // const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      // const link = document.createElement("a");
      // link.href = URL.createObjectURL(blob);
      // link.setAttribute("download", "exported_data.csv");
      // document.body.appendChild(link);
      // link.click();
      // document.body.removeChild(link);
      // Convert JSON to sheet
      // Convert sheet to CSV string
      const csv = XLSX.utils.sheet_to_csv(worksheet);

      // Create blob
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

      // Create download link
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);

      // Generate dynamic file name
      const clientName =
        selectedClient?.selectedClient?.name || "unknown-client";

      const safeClientName = clientName
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/gi, "-");

      const today = new Date();
      const day = String(today.getDate()).padStart(2, "0");
      const month = String(today.getMonth() + 1).padStart(2, "0");
      const year = today.getFullYear();

      const formattedDate = `${day}-${month}-${year}`;
      const fileName = `${safeClientName}-${formattedDate}.csv`;

      link.setAttribute("download", fileName);

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Step 6: Update invoice stage
      // await Promise.all(
      //   selectedRows.map((row) => UpdateInvoiceStage(3, row.id)),
      // );

      makeInvoicesExported(
        selectedRows.map((row) => {
          return {
            invoiceId: row.id,
            invoicearchieveStatus: row.archiveStatus,
            invoiceStageId: row.stageId,
          };
        }),
      );

      if (includeDetails) {
        const archivePayload = selectedRows.map((row) => ({
          invoiceId: row.id,
          invoicearchieveStatus: row.archiveStatus,
          invoiceStageId: row.stageId,
        }));

        await makeInvoicesArchived(archivePayload);

        AlertMessage({
          text: "Invoices exported and archived successfully",
          type: "success",
        });
      } else {
        AlertMessage({
          text: "Invoices exported successfully",
          type: "success",
        });
      }

      await FetchData(); // Reload data after exporting
    } catch (error) {
      AlertMessage({
        text: "Error exporting data, Please try again",
        type: "error",
      });
    } finally {
      setLoading(false);
      await resetAndFetchWithCount(previouslyLoadedCount);
      setTimeout(() => {
        const wrapper = document.querySelector(".table-wrapper") as HTMLElement;
        if (wrapper) wrapper.scrollTop = savedScrollTop;
      }, 300);
    }
  };

  useEffect(() => {
    if (invoices.length === 0) {
      setSelectAll(false);
      return;
    }

    setSelectAll(selectedRows.length === invoices.length);
  }, [selectedRows, invoices]);

  const columns = useMemo(
    () => [
      {
        key: "selected",
        title: "selected",
        render: (
          <input
            type="checkbox"
            checked={selectAll}
            onChange={handleSelectAll}
          />
        ),
        dataIndex: "selected",
        isCheckbox: true,
        showSortArrows: false,
      },
      {
        key: "isFlagged",
        title: "isFlagged",
        dataIndex: "isFlagged",
        showSortArrows: false,
        isFlag: true,
      },
      {
        title: "ID",
        dataIndex: "id",
        key: "invoiceId",
        sorter: (a: RowData, b: RowData) => a.id - b.id,
      },
      {
        title: "Status",
        dataIndex: "stageId",
        key: "Status",
        isStatus: true,
        //sorter: (a: RowData, b: RowData) => a.stageId - b.stageId,
        // render: (stageId: number) => SetStatus(stageId),
        showSortArrows: true,
      },
      {
        title: "Submitted By",
        dataIndex: "submittedBy",
        key: "submittedBy",
        showSortArrows: true,
        render: (text: string | null, record: any) =>
          record.submissionMethod?.toLowerCase() === "email"
            ? ""
            : text || "—",
      },
      {
        title: "Date",
        dataIndex: "invoiceDate",
        key: "invoiceDate",
        isDate: true,
        showSortArrows: true,
        render: (text: string) => formatForInput(text),
        // const date = new Date(text); // Parse the ISO date
        // const formattedDate = date.toLocaleDateString("en-GB", {
        //   day: "2-digit",
        //   month: "2-digit",
        //   year: "numeric",
        // }); // Formats as dd/mm/yyyy
        // return (
        //   <Tooltip title={formattedDate}>
        //     <span>{formattedDate}</span>
        //   </Tooltip>
        // );
      },
      {
        title: "Supplier",
        dataIndex: "supplier",
        key: "supplier",
        sorter: (a: RowData, b: RowData) =>
          a.supplier.localeCompare(b.supplier),
      },
      {
        title: "Total",
        dataIndex: "totalAmount",
        key: "totalAmount",
        sorter: (a: RowData, b: RowData) => a.totalAmount - b.totalAmount,
        render: (discountValue: number) => (
          <Tooltip title={discountValue}>
            <span>
              {discountValue != null ? discountValue.toFixed(2) : "0.00"}
            </span>
          </Tooltip>
        ),
      },

      {
        title: "Tax",
        dataIndex: "taxAmount",
        key: "TAXAMOUNT",
        showSortArrows: true,
        // showSortArrows: true,
        // render: (taxAmount: number) => ()
      },
      {
        title: "Document Ref",
        dataIndex: "documentReference",
        key: "documentReference",
        sorter: (a: RowData, b: RowData) =>
          a.documentReference.localeCompare(b.documentReference),
        showSortArrows: false,
      },
      {
        title: "User",
        dataIndex: "documentOwner",
        key: "documentOwner",

        sorter: (a: RowData, b: RowData) =>
          a.documentOwner.localeCompare(b.documentOwner),
        showSortArrows: false,
      },
      // {
      //   title: "Admin User ID",
      //   dataIndex: "adminUserId",
      //   key: "adminUserId",
      //   sorter: (a: RowData, b: RowData) =>
      //     (a.adminUserId || "").localeCompare(b.adminUserId || ""),
      // },
      {
        title: "Document URL",
        dataIndex: "documentShortId",
        key: "documentShortId",
        isUrl: true,
        showSortArrows: false,
        // render: (text: string) =>
        //   text ? (
        //     <a href={text} target="_blank" rel="noopener noreferrer">
        //       Link
        //     </a>
        //   ) : (
        //     <span>N/A</span>
        //   ),
      },
      // {
      //   title: "Document Owner",
      //   dataIndex: "documentOwner",
      //   key: "documentOwner",
      //   sorter: (a: RowData, b: RowData) =>
      //     a.documentOwner.localeCompare(b.documentOwner),
      // },
      // {
      //   title: "Type",
      //   dataIndex: "type",
      //   key: "type",
      //   sorter: (a: RowData, b: RowData) => a.type.localeCompare(b.type),
      //   showSortArrows: false,
      // },
      {
        title: "Type",
        dataIndex: "type",
        key: "type",
        // isStatus: true,
        showSortArrows: false,
      },
      {
        title: "Category",
        dataIndex: "category",
        key: "category",
        // isStatus: true,
        showSortArrows: true,
      },
      {
        title: "Description",
        dataIndex: "description",
        key: "Description",
        showSortArrows: true,
        sorter: (a: RowData, b: RowData) =>
          a.description.localeCompare(b.description),
        render: (text: string) => (
          <Tooltip title={text}>
            <span>
              {text.length > 15 ? `${text.substring(0, 15)}...` : text}
            </span>
          </Tooltip>
        ),
      },
      {
        title: "Invoice Date",
        dataIndex: "invoiceDate",
        key: "invoiceDate",
        isDate: true,
        sorter: (a: RowData, b: RowData) =>
          new Date(a.invoiceDate).getTime() - new Date(b.invoiceDate).getTime(),
        render: (text: string) => "fsgfsfs", //formatForInput(text),
        // {
        //   const date = new Date(text); // Parse the ISO date
        //   const formattedDate = date.toLocaleDateString("en-GB", {
        //     day: "2-digit",
        //     month: "2-digit",
        //     year: "numeric",
        //   }); // Formats as dd/mm/yyyy
        //   return (
        //     <Tooltip title={formattedDate}>
        //       <span>{formattedDate}</span>
        //     </Tooltip>
        //   );
        // }
      },

      {
        title: "Extracted PO Number",
        dataIndex: "extractedPoNumber",
        key: "extractedPoNumber",
        sorter: (a: RowData, b: RowData) =>
          a.extractedPoNumber.localeCompare(b.extractedPoNumber),
        showSortArrows: false,
      },

      {
        title: "Due Date",
        dataIndex: "dueDate",
        key: "dueDate",
        isDate: true,
        showSortArrows: true,
        sorter: (a: RowData, b: RowData) =>
          new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime(),
        render: (text: string) => {
          const date = new Date(text); // Parse the ISO date
          const formattedDate = date.toLocaleDateString("en-GB", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          }); // Formats as dd/mm/yyyy
          return (
            <Tooltip title={formattedDate}>
              <span>{formattedDate}</span>
            </Tooltip>
          );
        },
      },
      // {
      //   title: "Category",
      //   dataIndex: "category",
      //   key: "category",
      //   sorter: (a: RowData, b: RowData) =>
      //     a.category.localeCompare(b.category),
      //    showSortArrows: true,
      // },
      {
        title: "Product or Service",
        dataIndex: "productOrService",
        key: "productOrService",
        sorter: (a: RowData, b: RowData) =>
          a.productOrService.localeCompare(b.productOrService),
        showSortArrows: false,
      },
      // {
      //   title: "User",
      //   dataIndex: "user",
      //   key: "user",
      //   sorter: (a: RowData, b: RowData) =>
      //     a.customer.localeCompare(b.customer),
      // },
      {
        title: "Mark as Rebillable",
        dataIndex: "markAsRebillable",
        key: "markAsRebillable",
        // sorter: (a: RowData, b: RowData) =>
        //   a.markAsRebillable.localeCompare(b.markAsRebillable),
        showSortArrows: false,
      },

      {
        title: "Currency",
        dataIndex: "currency",
        key: "currency",
        showSortArrows: false,
        // sorter: (a: RowData, b: RowData) =>
        //   a.currency.localeCompare(b.currency),
      },

      {
        title: "Tax Amount",
        dataIndex: "taxAmount",
        key: "taxAmount",
        showSortArrows: false,
        sorter: (a: RowData, b: RowData) => a.taxAmount - b.taxAmount,
        render: (discountValue: number) => (
          <Tooltip title={discountValue}>
            <span>
              {discountValue != null ? discountValue.toFixed(2) : "0.00"}
            </span>
          </Tooltip>
        ),
      },
      {
        title: "Net Amount",
        dataIndex: "netAmount",
        key: "netAmount",
        showSortArrows: false,
        sorter: (a: RowData, b: RowData) => a.netAmount - b.netAmount,
        render: (discountValue: number) => (
          <Tooltip title={discountValue}>
            <span>
              {discountValue != null ? discountValue.toFixed(2) : "0.00"}
            </span>
          </Tooltip>
        ),
      },
      // {
      //   title: "Payment",
      //   dataIndex: "payment",
      //   key: "payment",
      //   showSortArrows: false,
      //   sorter: (a: RowData, b: RowData) => a.payment - b.payment,
      //   render: (discountValue: number) => (
      //     <Tooltip title={discountValue}>
      //       <span>
      //         {discountValue != null ? discountValue.toFixed(2) : "0.00"}
      //       </span>
      //     </Tooltip>
      //   ),
      // },
      // {
      //   title: "Paid",
      //   dataIndex: "paid",
      //   key: "paid",
      //   showSortArrows: false,
      //   sorter: (a: RowData, b: RowData) => a.paid.localeCompare(b.paid),
      // },
      {
        title: "Discount",
        dataIndex: "discountAmount",
        key: "discountAmount",
        sorter: (a: RowData, b: RowData) => a.discountAmount - b.discountAmount,
        render: (discountValue: number) => (
          <Tooltip title={discountValue}>
            <span>
              {discountValue != null ? discountValue.toFixed(2) : "0.00"}
            </span>
          </Tooltip>
        ),
      },
      // {
      //   title: "Payment Method",
      //   dataIndex: "paymentMethod",
      //   key: "paymentMethod",
      //   showSortArrows: false,
      //   sorter: (a: RowData, b: RowData) =>
      //     a.paymentMethod.localeCompare(b.paymentMethod),
      // },

      {
        title: "Line Items",
        dataIndex: "lineItems",
        key: "lineItems",
        showSortArrows: false,
        sorter: (a: RowData, b: RowData) =>
          a.lineItems.length - b.lineItems.length,
        render: (items: any[]) =>
          items && items.length > 0 ? (
            <span>{items.join(", ")}</span>
          ) : (
            <span>No Items</span>
          ),
      },

      {
        title: "Is Active",
        dataIndex: "isActive",
        key: "isActive",
        showSortArrows: false,
        sorter: (a: RowData, b: RowData) => (a.isActive ? 1 : -1),
        render: (isActive: boolean) => (isActive ? "Yes" : "No"),
      },
      {
        title: "Is Delete",
        dataIndex: "isDelete",
        key: "isDelete",
        showSortArrows: false,
        // sorter: (a: RowData, b: RowData) => (a.isDelete ? 1 : -1),
        // render: (isDelete: boolean) => (isDelete ? "Yes" : "No"),
      },
      // {
      //   title: "Due Date",
      //   dataIndex: "dueDate",
      //   key: "dueDate",
      //   // isStatus: true,
      //   showSortArrows: true,
      //   isDate: true,
      //   render: (text: string) => {
      //     const date = new Date(text); // Parse the ISO date
      //     const formattedDate = date.toLocaleDateString("en-GB", {
      //       day: "2-digit",
      //       month: "2-digit",
      //       year: "numeric",
      //     }); // Formats as dd/mm/yyyy
      //     return (
      //       <Tooltip title={formattedDate}>
      //         <span>{formattedDate}</span>
      //       </Tooltip>
      //     );
      //   },
      // },
      {
        title: "Paid",
        dataIndex: "paid",
        key: "Paid",
        // isStatus: true,
        showSortArrows: true,
      },
      {
        title: "Payment",
        dataIndex: "payment",
        key: "Payment",
        // isStatus: true,
        showSortArrows: true,
      },
      {
        title: "Submission Method",
        dataIndex: "Submission Method",
        key: "Submission Method",
        // isStatus: true,
        showSortArrows: true,
      },
      {
        title: "Upload Date",
        dataIndex: "createdDate",
        key: "createdDate",
        isDate: true,
        // isStatus: true,
        showSortArrows: true,
      },
    ],
    [selectAll, selectedRows, currentRecords], // Recalculate columns only when relevant state changes
  );
  const selectedColumns: string[] = JSON.parse(
    localStorage.getItem("selectedColumns") || "[]",
  );

  //  Filter columns if selectedColumns exist, otherwise use default all columns
  const filteredColumns = selectedColumns.length
    ? columns.filter((col) => selectedColumns.includes(col.title as string))
    : columns; // if no selection, show all
  //* Display a warning  message if no client is selected
  if (!selectedClient.selectedClient?.userId) {
    return (
      <div>
        <h1>Please select a client to view their invoices in ready stage.</h1>
      </div>
    );
  }
  const onChangeTableParams: TableProps<any>["onChange"] = (
    pagination,
    filters,
    sorter: any,
    extra,
  ) => {
    handlePageChange(pagination.current ?? 1, pagination.pageSize ?? 10); // , pagination.pageSize ?? 10
  };

  const onRowCheckboxChange = (row: RowData, checked: boolean) => {
    setSelectedRows(
      (prevRows) =>
        checked
          ? prevRows.includes(row) // check if already selected
            ? prevRows
            : [...prevRows, row] // add full row object
          : prevRows.filter((r) => r.id !== row.id), // remove by id
    );
  };
  const isButtonActive = selectedRows.length > 0;
  const selectedInvoiceIds = selectedRows.map((row) => row.id);

  const handleFlagClick = async (row: RowData) => {
    const currentFlagState = flagOverrides[row.id] ?? row.isFlagged;
    const newFlagState = !currentFlagState;

    // Optimistic local update
    setFlagOverrides((prev) => ({ ...prev, [row.id]: newFlagState }));

    const result = await UpdateInvoiceFlagService(Number(row.id), newFlagState);

    if (!result.success) {
      // Revert on failure
      setFlagOverrides((prev) => ({ ...prev, [row.id]: currentFlagState }));
    }
  };
  const handleAdvancedApply = (searchFields: any) => {
    setActiveAdvancedSearch(searchFields);
    cursorRef.current = "";
    FetchData(false, false, null, activeFilters, toInvoiceSearchPayload(searchFields));
    setIsOpenAdvance(false);
  };

  const handleAdvancedReset = () => {
    setActiveAdvancedSearch(null);
    cursorRef.current = "";
    advancedSearchRef.current?.resetForm();
    FetchData(false, false, null, activeFilters, null);
  };

  const handleRemoveFilter = (key: keyof InvoiceFilterFieldsPayload) => {
    const updated = { ...activeFilters, [key]: null };
    setActiveFilters(updated);
    FetchData(false, false, null, updated, null);
  };

  const handleResetFilters = () => {
    setActiveFilters(null);
    setActiveAdvancedSearch(null);
    cursorRef.current = "";
    advancedSearchRef.current?.resetForm();
    setLoading(true);
    FetchData(false, false, null, null, null);
  };
  const handleAdvancedRemove = (updated: any) => {
    setActiveAdvancedSearch(updated);
    cursorRef.current = "";
    setInvoices([]);
    FetchData(false, false, null, activeFilters, toInvoiceSearchPayload(updated)); // Archive/Ready
  };

  const hasActiveSearch =
    searchTerm ||
    activeFilters ||
    (activeAdvancedSearch &&
      Object.values(activeAdvancedSearch).some((v) => v !== "" && v !== null));

  const isGenuinelyEmpty =
    invoices.length === 0 && !hasActiveSearch && !loading;
  const showToolbar = !isGenuinelyEmpty;

  return (
    <div className="Ready">
      {invoiceView ? (
        <ReadyInvoiceView
          setInvoiceView={(val) => {
            setInvoiceView(val);
            if (!val) {
              setTimeout(() => {
                const tableWrapper = document.querySelector(
                  ".table-wrapper",
                ) as HTMLElement;
                if (tableWrapper) {
                  tableWrapper.scrollTop = savedScrollPositionRef.current;
                }
              }, 50);
            }
          }}
          refreshInvoices={refreshInvoices}
          sortSettings={sortSettings}
          fullinvoices={fullinvoices}
          totalInvoiceCount={totalInvoiceCount}
        />
      ) : (
        <>
          {/* Export buttons */}
          <div className="selected-client-toolbar">
            <SelectedClientCard />
            {showToolbar && (
              <div className="buttons-group">
                {isOpen && (
                  <ColumnSettings
                    isOpen={true}
                    onClose={() => setIsOpen(false)}
                  />
                )}
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    padding: "5px 8px", // inner spacing
                    width: "300px", // adjust width
                    background: "#fff",
                  }}
                >
                  {/* Search Icon */}
                  <FiSearch size={18} style={{ color: "#474646" }} />
                  {/* Input Field */}
                  <input
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    style={{
                      flex: 1,
                      border: "none",
                      outline: "none",
                      fontSize: "var(--font-size-base)",
                    }}
                  />

                  {/* Advanced Button */}
                  <button
                    type="button"
                    onClick={() => {
                      setIsOpenAdvance(true);
                      if (cursorRef.current) {
                        cursorRef.current = "";
                      }
                    }}
                    className="advanced-btn"
                  >
                    {/* <button
                  type="button"
                  onClick={() => setIsOpenAdvance(true)}
                  className="advanced-btn"
                > */}
                    Advanced
                    <FiChevronDown style={{ marginLeft: "4px" }} />
                  </button>
                </div>
                <AdvancedSearch
                  setIsOpenAdvance={isOpenAdvance}
                  onClose={() => setIsOpenAdvance(false)}
                  FetchInvoices={FetchData}
                  onApply={handleAdvancedApply}
                  formRef={advancedSearchRef}
                  activeSearchFields={activeAdvancedSearch}
                />
                <FilterBtn
                  onClick={() => setIsFilterPopupOpen(true)}
                  disabled={loading}
                />
                {
                  <InvoiceFilter
                    activeFilters={activeFilters}
                    isOpen={isFilterPopupOpen}
                    onClose={() => setIsFilterPopupOpen(false)}
                    onApply={handleApplyFilters}
                  />
                }
                <FilterBtn
                  onClick={() => setIsOpen(true)}
                  disabled={loading}
                  Icon={IoSettingsSharp}
                />
              </div>
            )}
          </div>
          <div className="tm-row">
  <ToolsMenu
    selectedIds={selectedInvoiceIds}
    invoices={invoices}
    userId={selectedClient.selectedClient.userId}
    showMerge={false}
    onFlag={handleFlag}
    onUnflag={handleUnflag}
    onDelete={handleDelete}
    disabled={loading}
    screen="ready"
    bulkEditOptions={bulkEditOptions}
    onBulkEditComplete={handleBulkEditComplete}
    onViewFailedItems={handleViewFailedItems}
  />
  <button
    className="tm-trigger"
    disabled={!isButtonActive}
    onClick={moveToArchieve}
  >
    Archive
  </button>
  <CsvDownload
    csvData={selectedRows}
    exportToCSV={exportToCSV}
    exportDocHeaders={exportDocHeaders}
    // className="csv-download-button"
  />
  <button
    className="tm-trigger"
    onClick={() => setOpenPopup(true)}
    disabled={selectedRows.length === 0}
  >
    <DownloadOutlined />
    Export Xlsx
  </button>
  {failedView && (
    <span style={{ marginLeft: 12 }}>
      Showing items that could not be updated.{" "}
      <button type="button" onClick={refetchKeepingView}>Show all</button>
    </span>
  )}
</div>
          {showToolbar && (
            <ActiveFilterBar
              activeFilters={activeFilters}
              activeAdvancedSearch={activeAdvancedSearch}
              onRemove={handleRemoveFilter}
              onReset={handleResetFilters}
              onAdvancedReset={handleAdvancedReset}
              onAdvancedRemove={handleAdvancedRemove}
            />
          )}
          <ConfirmPopup
            blur
            isOpen={openPopup}
            title="Export Invoices as XLSX"
            message={
              <>
                <span
                  style={{
                    fontWeight: "bold",
                    display: "block",
                    marginBottom: "15px",
                  }}
                >
                  Export your Invoices as an XLSX file.
                </span>

                {/* Checkbox added below the export text */}
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "15px",
                    cursor: "pointer",
                    fontWeight: "normal",
                    fontSize: "var(--font-size-base)",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={includeDetails}
                    onChange={(e) => setIncludeDetails(e.target.checked)}
                    style={{ width: "16px", height: "16px", cursor: "pointer" }}
                  />
                  Archive items after export
                </label>
              </>
            }
            confirmLabel="Export"
            cancelLabel="Cancel"
            confirmColor="var(--color-brand)"
            onConfirm={exportToXlsx}
            onCancel={() => {
              setOpenPopup(false);
              setIncludeDetails(false);
              // setDeleteRow(null);
            }}
          />
          {invoices.length === 0 ? (
            <h1
              style={{ textAlign: "center", marginTop: "40px", color: "#888" }}
            >
              No invoices found for this client
            </h1>
          ) : (
            <CommonTable
              infinityScroll={true}
              loadMoreInvoicesOnScroll={loadMoreInvoicesOnScroll}
              columns={filteredColumns}
              onFlagClick={handleFlagClick}
              // data={invoices}
              selectedRowKeys={selectedRows.map((row) => row.id)}
              onRowCheckboxChange={onRowCheckboxChange}
              selectAll={selectAll}
              data={invoices.map((row: RowData) => ({
                ...row,
                isFlagged: flagOverrides[row.id] ?? row.isFlagged,
              }))}
              handleSelectAll={handleSelectAll}
              stageId={2} // pass current stageId
              userId={selectedClient.selectedClient?.userId}
              sortInvoices={sortInvoices}
              // setInvoiceView={setInvoiceView}
              totalCount={totalInvoiceCount}
              onRow={(record: RowData) => ({
                onClick: (e) => {
                  //  If click comes from checkbox, do nothing
                  if (
                    (e.target as HTMLElement).closest("input[type='checkbox']")
                  ) {
                    return;
                  }

                  //  Otherwise open invoice
                  handleInvoiceClick(record);
                },
              })}
            />
          )}

          <h2 style={{ textAlign: "center", color: "var(--color-brand)" }}>
            {isLoadingMore ? " Loading... " : invoices.length} /{" "}
            {totalInvoiceCount}{" "}
          </h2>
        </>
      )}
    </div>
  );
};

export default Ready;
